// REACT
import { useState, useEffect } from "react";

// REDUX
import { useDispatch } from "react-redux";
import { reset2FAStateThunk, useUserSlice } from "src/store/features/user.slice";
import {
  useAddBankDetailsMutation,
  IBankDetailsRes,
  useSendEmailCodeMutation,
} from "src/store/features/api/generated";
import { setError, setStep, useBeneficiarySlice } from "src/store/features/beneficiary.slice";
import api from "src/store/features/api";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// HOOKS
import usePermission from "../usePermission";

interface IBankAccount extends IBankDetailsRes {
  isOpen: boolean;
}

export const useBankDetails = () => {
  const dispatch = useDispatch();
  const { isGuest } = usePermission();

  const beneficiaryState = useBeneficiarySlice();

  const { is2FAEnabled, emailCode, code2fa, isEmailCodeSuccess } = useUserSlice();

  const { data: bankAccounts, isLoading: isBankDetailsLoading } = api.useGetBankDetailsQuery(
    undefined,
    { skip: isGuest },
  );

  const [addBankDetails, { isLoading: isAddingBankDetailsLoading }] = useAddBankDetailsMutation();
  const [sendEmail, { isLoading: isSendingEmail }] = useSendEmailCodeMutation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [accountStates, setAccountStates] = useState<IBankAccount[]>([]);

  useEffect(() => {
    if (bankAccounts) {
      setAccountStates(
        bankAccounts.map((bankAccount) => ({
          ...bankAccount,
          isOpen: false,
        })),
      );
    }
  }, [bankAccounts]);

  const toggleAccountVisibility = (index: number) => {
    setAccountStates((prevStates: IBankAccount[]) =>
      prevStates?.map((state: IBankAccount, i: number) =>
        i === index ? { ...state, isOpen: !state.isOpen } : state,
      ),
    );

    trackEvent(
      "User toggled bank account visibility",
      {
        isOpen: accountStates[index].isOpen,
      },
      "Payment",
    );
  };

  const handleSubmitBankDetails = async () => {
    trackEvent("User clicked on Submit 2FA details", {}, "Payment");

    if (!emailCode || !code2fa) {
      dispatch(setError("Please enter the email code and 2FA code."));
      return;
    }

    dispatch(setError(""));
    await addBankDetails({
      bankAccountReqDto: {
        ...beneficiaryState,
        currency: beneficiaryState.currency?.value,
        emailCode,
        code: code2fa,
      },
    })
      .unwrap()
      .then(({ currency, trailingAccNumberDigits }) => {
        setAccountStates((prevStates) => [
          ...prevStates,
          {
            currency,
            trailingAccNumberDigits,
            isOpen: false,
          },
        ]);
        setIsModalOpen(!isModalOpen);
        dispatch(setStep("Complete"));
        dispatch(reset2FAStateThunk());
        trackEvent("User completed adding bank details", {}, "Payment");
      })
      .catch((e) => {
        dispatch(setError(e.message));
        trackEvent("Error when adding User bank details", {}, "Payment");
      });
  };

  const handle2faVerification = async () => {
    trackEvent("User clicked on Submit Bank Details. 2FA Modal Initiated", {}, "Payment");

    await sendEmail({
      twoFaEmailVerificationDto: {
        emailTemplate: "bankDetails2FA",
      },
    })
      .then(() => {
        setIsModalOpen(!isModalOpen);
        trackEvent("Success sending User email 2FA code", {}, "Payment");
      })
      .catch((e) => {
        dispatch(setError("There was an issue sending the email code. Please try again."));
        trackEvent("Error sending User email 2FA code", {}, "Payment");
      });
  };

  const handleModal = () => {
    trackEvent("User opened/closed 2FA modal", {}, "Payment");
    dispatch(setError(""));
    setIsModalOpen(!isModalOpen);
  };

  const isButtonDisabled =
    !is2FAEnabled || isAddingBankDetailsLoading || !isEmailCodeSuccess || code2fa?.length !== 6;

  const isInitialLoading = isBankDetailsLoading;

  return {
    bankAccounts,
    accountStates,
    isModalOpen,
    isBankDetailsLoading,
    isInitialLoading,
    isButtonDisabled,
    isAddingBankDetailsLoading,
    toggleAccountVisibility,
    handleSubmitBankDetails,
    handle2faVerification,
    isSendingEmail,
    handleModal,
  };
};
